/**
 * Hooks for the Vehicle Search page:
 *   - useVehicleSearch    → GET /vehicles?q=     (registration substring search)
 *   - useVehicleTimeline  → GET /vehicles/{reg}/timeline   (fuel/oil/toll touch-points)
 *
 * The search reuses the existing list endpoint's `q` filter (ILIKE on
 * registration/make/model). The timeline is always asset_token-scoped server
 * side, so it rides the (asset_token, transaction_time) hypertable index.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@/api/client';
import type {
  VehicleListResponse,
  VehicleTimelineResponse,
} from '@/types/vehicles';

const STALE_MS = 30_000;

/** Search vehicles by registration (min 2 chars before it fires). */
export function useVehicleSearch(
  query: string,
  limit = 20,
): UseQueryResult<VehicleListResponse, Error> {
  const q = query.trim();
  return useQuery<VehicleListResponse, Error>({
    queryKey: ['vehicle-search', q, limit],
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data } = await api.get<VehicleListResponse>('/vehicles', {
        params: { q, limit },
      });
      return data;
    },
    staleTime: STALE_MS,
  });
}

/** Per-vehicle fuel/oil/toll touch-point timeline (newest first). */
export function useVehicleTimeline(
  registration: string | undefined,
  opts?: { limit?: number; start?: string; end?: string },
): UseQueryResult<VehicleTimelineResponse, Error> {
  return useQuery<VehicleTimelineResponse, Error>({
    queryKey: ['vehicle-timeline', registration, opts?.limit, opts?.start, opts?.end],
    enabled: Boolean(registration),
    queryFn: async () => {
      const { data } = await api.get<VehicleTimelineResponse>(
        `/vehicles/${encodeURIComponent(registration as string)}/timeline`,
        { params: { limit: opts?.limit ?? 100, start: opts?.start, end: opts?.end } },
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}
